import { ResultGroup } from '@src/suite/common/data-models/search-results/result-group';
import { TreeLikeFlatNode, TreeLikeNode } from '@src/suite/common/data-models/search-results/search-results';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';

export const getNodeLevel = ({ level }: TreeLikeFlatNode<StorytellingRecord>) => {
    return level;
};

export const getStoryNodeLevel = ({ level }: TreeLikeFlatNode<StorytellingStoryRecord>) => {
    return level;
};

// Function that determines whether a flat node is expandable or not
export const getIsNodeExpandable = ({ hasChildren }: TreeLikeFlatNode<StorytellingRecord>) => {
    return hasChildren;
};

export const getStoryIsNodeExpandable = ({ hasChildren }: TreeLikeFlatNode<StorytellingStoryRecord>) => {
    return hasChildren;
};

// Function that returns a nested node's list of children
export const getNodeChildren = ({ children }: TreeLikeNode<StorytellingRecord>) => {
    return children;
};

export const getStoryNodeChildren = ({ children }: TreeLikeNode<StorytellingStoryRecord>) => {
    return children;
};

// Function that maps a nested node to a flat node
export const nodeTransformer = (node: TreeLikeNode<StorytellingRecord>, level: number): TreeLikeFlatNode<StorytellingRecord> => {
    return {
        id: node.id,
        label: node.label,
        uniqueId: node.uniqueId,
        index: node.index,
        itemsCount: node.itemsCount,
        level,
        hasChildren: node?.children?.length > 0,
        object: node.object,
        nodeLastInGroup: node.nodeLastInGroup,
        nodeFirstInGroup: node.nodeFirstInGroup,
        linkedRecordType: node?.linkedRecordType,
        isMetadata: node?.isMetadata,
        isNewItems: node?.isNewItems,
        totalCost: node?.totalCost,
        isDisabled: isDisabled(node.object),
    };
};

export const getObjectTypeByViewStateType = (vstype: ViewStateTypesEnum, guid?: string): ObjectTypes => {
    if (vstype === ViewStateTypesEnum.TOPIC) {
        return ObjectTypes.TOPIC;
    }
    if (vstype === ViewStateTypesEnum.ASSIGNMENT) {
        return ObjectTypes.ASSIGNMENT;
    }

    if (vstype === ViewStateTypesEnum.SCRIPT) {
        return ObjectTypes.SCRIPT;
    }

    if (guid) {
        return ObjectTypes.UNASSIGNED;
    }
    return null;
};

export const getTopicsTreeLikeData = (topics: ResultGroup<StorytellingTopicRecord>): TreeLikeNode<StorytellingTopicRecord>[] => {
    return topics.keys.map(key => {
        let totalCost = 0;
        const children = topics.entries[key.value].reduce((acc, entry, index) => {
            totalCost += entry?.totalCost;
            const res = {
                id: entry?.Guid,
                uniqueId: `${key.value}-${entry?.Guid}`,
                children: [],
                object: entry,
                nodeFirstInGroup: index === 0,
                nodeLastInGroup: index === topics.entries[key.value].length - 1,
            };
            return [...acc, res];
        }, []);
        return {
            id: key.value,
            uniqueId: key.value,
            label: key.display,
            itemsCount: children.length,
            totalCost,
            children,
        };
    });
};

export const getStoriesTreeLikeData = (stories: ResultGroup<StorytellingStoryRecord>): TreeLikeNode<StorytellingTopicRecord>[] => {
    return stories.keys.map(key => ({
        id: key.value,
        uniqueId: key.value,
        label: key.display,
        itemsCount: stories.entries[key.value].length,
        children: stories.entries[key.value].map((entry, index) => ({
            id: entry?.Guid,
            uniqueId: `${key.value}-${entry?.Guid}`,
            children: [],
            object: entry,
            nodeFirstInGroup: index === 0,
            nodeLastInGroup: index === stories.entries[key.value].length - 1,
        })),
    }));
};

export const getAssignmentsTreeLikeData = (assignments: ResultGroup<StorytellingAssignmentRecord>): TreeLikeNode<StorytellingTopicRecord>[] => {
    return assignments.keys.map(key => {
        let totalCost = 0;
        const children = assignments.entries[key.value].reduce((acc, entry, index) => {
            const parent = acc.find(item => item.id === entry.ParentGuid);
            if (!parent) {
                totalCost += entry?.totalCost;
                acc.push({
                    id: entry.ParentGuid,
                    label: entry.ContainerTitle,
                    object: entry,
                    uniqueId: `${key.value}-${entry?.ParentGuid}`,
                    isMetadata: true,
                    children: [
                        {
                            id: entry.Guid,
                            children: [],
                            object: entry,
                            uniqueId: `${key.value}-${entry?.Guid}`,
                            nodeFirstInGroup: index === 0,
                            nodeLastInGroup: index === assignments.entries[key.value].length - 1,
                        },
                    ],
                });
            } else {
                totalCost += entry?.totalCost;
                parent.children.push({
                    id: entry.Guid,
                    children: [],
                    object: entry,
                    uniqueId: `${key.value}-${entry?.Guid}`,
                    nodeFirstInGroup: index === 0,
                    nodeLastInGroup: index === assignments.entries[key.value].length - 1,
                });
            }
            return acc;
        }, []);

        return {
            id: key.value,
            label: key.display,
            uniqueId: key.value,
            itemsCount: assignments.entries[key.value].length,
            totalCost,
            children,
        };
    });
};

export const getRecordsTreeLikeData = (records: ResultGroup<StorytellingRecord>): TreeLikeNode<StorytellingRecord>[] => {
    return records.keys.map(key => ({
        id: key.value,
        uniqueId: key.value,
        label: key.display,
        itemsCount: records.entries[key.value].length,
        children: records.entries[key.value].map((entry, index) => ({
            id: entry?.Guid,
            uniqueId: `${key.value}-${entry?.Guid}`,
            children: [],
            object: entry,
            nodeFirstInGroup: index === 0,
            nodeLastInGroup: index === records.entries[key.value].length - 1,
        })),
    }));
};

export const getSearchResultsTreeLikeData = (records: ResultGroup<StorytellingRecord>): TreeLikeNode<StorytellingRecord>[] => {
    const recordsValues = Object.values(records);
    let search = [];
    recordsValues.forEach((record: ResultGroup<StorytellingRecord>) => {
        if (record && record.entries.SingleGroup && record.entries.SingleGroup.length > 0) {
            let totalCost = 0;
            const children = record.entries.SingleGroup.map<TreeLikeNode<StorytellingRecord>>((entry, idx) => {
                totalCost += entry?.totalCost;
                return {
                    id: entry.Guid,
                    uniqueId: `${'topics'}-${entry?.Guid}`,
                    children: [],
                    object: entry,
                    nodeFirstInGroup: idx === 0,
                    nodeLastInGroup: idx === record.entries.SingleGroup.length - 1,
                };
            });

            const topics = {
                id: 'topics',
                label: 'Topics',
                itemsCount: record.entries.SingleGroup.length,
                uniqueId: 'topics',
                totalCost,
                children,
            };
            search = [...search, topics];
        }
        if (record && record.keys && record.keys.length > 0 && !record.entries.SingleGroup) {
            let totalCost = 0;
            const assignmentsAndStories = record.keys.reduce((grpAcc, key) => {
                const childrenReducer = (childrenAcc, entry, index) => {
                    const parent = childrenAcc.find(item => item.id === entry.ParentGuid);
                    if (!parent && entry.Type !== ObjectTypes.SCRIPT) {
                        totalCost += entry?.totalCost;
                        childrenAcc.push({
                            id: entry.ParentGuid,
                            label: entry.topicMetadata?.Title,
                            object: entry,
                            uniqueId: `assignments-${entry.ParentGuid}-${entry.Guid}`,
                            isMetadata: true,
                            children: [
                                {
                                    id: entry.Guid,
                                    children: [],
                                    object: entry,
                                    uniqueId: `assignments-${entry.Guid}`,
                                    nodeFirstInGroup: index === 0,
                                    nodeLastInGroup: false,
                                },
                            ],
                        });
                    } else {
                        childrenAcc.push({
                            id: entry.Guid,
                            children: [],
                            object: entry,
                            uniqueId: `stories-${entry.Guid}`,
                            nodeFirstInGroup: index === 0,
                            nodeLastInGroup: false,
                        });
                    }
                    return childrenAcc;
                };

                if (grpAcc.id) {
                    grpAcc.children = [...grpAcc.children, ...record.entries[key.value].reduce(childrenReducer, [] as TreeLikeNode<StorytellingRecord>[])];
                } else {
                    const entries = record.entries[key.value];
                    entries.forEach(entry => {
                        if (entry.Type === ObjectTypes.ASSIGNMENT) {
                            grpAcc = {
                                id: 'assignments',
                                label: 'Assignments',
                                uniqueId: 'assignments',
                                itemsCount: entries.length,
                                children: entries.reduce(childrenReducer, [] as TreeLikeNode<StorytellingRecord>[]),
                            };
                        } else {
                            grpAcc = {
                                id: 'stories',
                                label: 'Stories',
                                uniqueId: 'stories',
                                itemsCount: entries.length,
                                children: entries.reduce(childrenReducer, [] as TreeLikeNode<StorytellingRecord>[]),
                            };
                        }
                    });
                }
                return grpAcc;
            }, {} as TreeLikeNode<StorytellingRecord>);
            assignmentsAndStories.itemsCount = assignmentsAndStories.children.length;
            assignmentsAndStories.totalCost = totalCost;
            search = [...search, assignmentsAndStories];
        }
    });
    return search;
};
const isDisabled = (object: StorytellingRecord) => {
    if (object?.Type === ObjectTypes.SCRIPT) {
        return (object as StorytellingStoryRecord)?.topicMetadata?.Path?.isWasteBinPath;
    } else if (object?.Type === ObjectTypes.ASSIGNMENT) {
        return (object as StorytellingAssignmentRecord)?.topicMetadata?.Path?.isWasteBinPath;
    }
    if (object?.Guid && object?.Path?.isWasteBinPath) {
        return true;
    }
    return false;
};
